"use client";

import { useEffect, useState, useSyncExternalStore } from "react";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { usePathname } from "next/navigation";
import Manager from "@/components/Manager";
import Sidebar from "@/components/Sidebar";
import SidePanel, { usePanelState } from "@/components/SidePanel";
import { ManagerIcon, MenuIcon, PanelIcon, SearchIcon } from "@/components/icons";
import CommandPalette from "@/components/CommandPalette";
import OnboardingWizard from "@/components/OnboardingWizard";
import { HeaderControlsContext } from "@/components/PageHeader";
import { ToolRequestToasts, ToolRequestsProvider } from "@/components/ToolRequests";

function subscribeDesktop(onChange: () => void) {
  const query = window.matchMedia("(min-width: 768px)");
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
const desktopSnapshot = () => window.matchMedia("(min-width: 768px)").matches;
const serverDesktopSnapshot = () => false;

export default function AppShell({ children, businessId }: { children: React.ReactNode; businessId: string | null }) {
  const desktop = useSyncExternalStore(subscribeDesktop, desktopSnapshot, serverDesktopSnapshot);
  const pathname = usePathname();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const sidebar = usePanelState({
    widthKey: "caw:sidebar-width",
    hiddenKey: "caw:sidebar-hidden",
    defaultWidth: 320,
    side: "left",
  });
  const manager = usePanelState({
    widthKey: "caw:manager-width",
    hiddenKey: "caw:manager-hidden",
    defaultWidth: 384,
    side: "right",
  });

  // Following a link from inside a drawer should reveal the page it opened,
  // not leave the drawer sitting on top of it.
  const closeSidebarDrawer = sidebar.setDrawer;
  const closeManagerDrawer = manager.setDrawer;
  useEffect(() => {
    closeSidebarDrawer(false);
    closeManagerDrawer(false);
  }, [pathname, closeSidebarDrawer, closeManagerDrawer]);

  if (
    pathname === "/" ||
    pathname === "/login" ||
    pathname === "/signup" ||
    pathname === "/forgot" ||
    pathname.startsWith("/reset/") ||
    pathname.startsWith("/invite/")
  ) {
    return <ConfirmProvider>{children}</ConfirmProvider>;
  }

  return (
    <ConfirmProvider>
    <ToolRequestsProvider businessId={businessId}>
    <div data-app-shell className="flex h-dvh overflow-hidden">
      <SidePanel side="left" state={sidebar} label="Navigation">
        <Sidebar
          visible={desktop ? !sidebar.hidden : sidebar.drawer}
          onHide={() => sidebar.setHidden(true)}
          onCloseDrawer={() => sidebar.setDrawer(false)}
        />
      </SidePanel>

      <main className="@container relative flex min-w-0 flex-1 flex-col overflow-hidden">

        <HeaderControlsContext.Provider value={{
          navigation: (
            <button
              type="button"
              onClick={() => window.matchMedia("(min-width: 768px)").matches ? sidebar.setHidden(false) : sidebar.setDrawer(true)}
              aria-label="Open navigation"
              title="Open navigation"
              className={`flex size-8 shrink-0 items-center justify-center rounded-lg text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-100 ${sidebar.hidden ? "" : "md:hidden"}`}
            >
              {sidebar.hidden ? <PanelIcon className="size-4" /> : <MenuIcon className="size-4" />}
            </button>
          ),
          actions: (
            <div className="flex shrink-0 items-center gap-1" aria-label="Page tools">
              <button
                type="button"
                onClick={() => setPaletteOpen(true)}
                aria-label="Search commands and workers"
                title="Search commands and workers (⌘K / Ctrl+K)"
                className="flex h-8 items-center justify-center gap-2 rounded-lg border border-zinc-800 px-2 text-xs text-zinc-400 transition hover:bg-zinc-900 hover:text-zinc-100"
              >
                <SearchIcon className="size-4" />
                <span className="hidden @[64rem]/header:inline">Search</span>
                <kbd className="hidden rounded border border-zinc-700 px-1 text-[10px] @[64rem]/header:inline">⌘K</kbd>
              </button>
              <button
                type="button"
                onClick={() => setWizardOpen(true)}
                aria-label="Open quick start guide"
                title="Quick Start Guide"
                className="flex h-8 items-center justify-center gap-1.5 rounded-lg px-2 text-xs text-zinc-400 transition hover:bg-zinc-900 hover:text-violet-300"
              >
                <span aria-hidden className="text-base">✦</span>
                <span className="hidden @[64rem]/header:inline">Quick Start</span>
              </button>
              <button
                type="button"
                onClick={() => window.matchMedia("(min-width: 768px)").matches ? manager.setHidden(false) : manager.setDrawer(true)}
                aria-label="Open Smithers"
                title="Open Smithers"
                className={`flex size-8 items-center justify-center rounded-lg text-zinc-400 transition hover:bg-zinc-800 hover:text-zinc-100 ${manager.hidden ? "" : "md:hidden"}`}
              >
                <ManagerIcon className="size-4" />
              </button>
            </div>
          ),
        }}>
          {children}
        </HeaderControlsContext.Provider>
        <ToolRequestToasts />
      </main>

      <SidePanel side="right" state={manager} label="Smithers">
        <Manager
          key={businessId ?? "no-business"}
          businessId={businessId}
          visible={desktop ? !manager.hidden : manager.drawer}
          onHide={() => manager.setHidden(true)}
          onCloseDrawer={() => manager.setDrawer(false)}
        />
      </SidePanel>

      {/* Global Command Palette modal */}
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />

      {/* Onboarding Wizard modal */}
      {wizardOpen && <OnboardingWizard isOpen={true} onClose={() => setWizardOpen(false)} />}


    </div>
    </ToolRequestsProvider>
    
    </ConfirmProvider>
  );
}
